// src/components/Chat.tsx
import React, { useState, useEffect, useRef, useCallback, Fragment, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { PanelLeft, Plus } from 'lucide-react';
import { chatApi, ChatMessage, ChatResponse, Conversation } from '../services/chatApi';
import { extractFromPhoto } from '../services/photoApi';
// PDFViewer pulls in pdfjs-dist (~150-300 KB client + a 1.2 MB worker).
// Defer until the user actually opens a source preview.
const PDFViewer = lazy(() => import('./documents/PDFViewer'));
import MessageBubble from './chat/MessageBubble';
import ChatInput, { ChatInputHandle } from './chat/ChatInput';
import ChatHomeState from './chat/ChatHomeState';
import ConversationList from './chat/ConversationList';
import clsx from 'clsx';
import { shouldShowVoiceNotice, markVoiceNoticeSeen } from './chat/VoiceFirstUseNotice';
import { useVoiceInput } from '../hooks/useVoiceInput';
import toast from 'react-hot-toast';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis'; // Task 13; no-op outside its provider
import { useAuth } from '../hooks/useAuth';
import { useVoicePreferences } from '../hooks/useVoicePreferences';

export const Chat: React.FC = () => {
  const { conversationId: convIdParam } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [streamingPhase, setStreamingPhase] = useState<'rewriting' | 'searching' | 'generating' | 'streaming' | null>(null);

  // PDF Preview state
  const [previewDocument, setPreviewDocument] = useState<{
    id: number;
    name: string;
    pageNumber?: number;
  } | null>(null);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);

  // Ref to accumulate streamed tokens without triggering a re-render per token.
  // A flush interval reads from the ref and pushes the accumulated text into
  // React state in batches (~60 fps), keeping the UI smooth.
  const streamBufferRef = useRef<string>('');
  const streamingMessageIdRef = useRef<string | null>(null);
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const firstTokenRef = useRef<boolean>(true);
  // Accumulates EVERY streamed token for auto-read (Codex P1-7). The 16ms-flushed
  // streamBufferRef is cleared on each flush, so it does NOT hold the full answer
  // at onDone — this ref does.
  const fullAnswerRef = useRef<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Imperative handle so a quick-action chip can focus the input after prefilling it.
  const chatInputRef = useRef<ChatInputHandle>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // URL-based conversation loading
  useEffect(() => {
    if (convIdParam) {
      const id = parseInt(convIdParam, 10);
      if (!isNaN(id) && id !== currentConversationId) {
        invalidateRecordToken();   // discard a transcript resolving after a URL-driven conversation change (Codex P1-6)
        loadConversationMessages(id);
      }
    }
  }, [convIdParam]);

  const loadConversations = async () => {
    try {
      const convs = await chatApi.getConversations();
      setConversations(convs);
      console.log('📋 Loaded conversations:', convs);
    } catch (error) {
      console.error('❌ Error loading conversations:', error);
    }
  };

  const loadConversationMessages = async (conversationId: number) => {
    try {
      const { messages: convMessages } = await chatApi.getConversationMessages(conversationId);
      setMessages(convMessages);
      setCurrentConversationId(conversationId);
      console.log('📨 Loaded messages for conversation:', conversationId, convMessages);
    } catch (error) {
      console.error('❌ Error loading conversation messages:', error);
    }
  };

  // ---- Streaming helpers ----

  /** Flush accumulated tokens from the ref into the React state message. */
  const startFlushInterval = useCallback(() => {
    // Clear any existing interval to avoid duplicates.
    if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);

    flushIntervalRef.current = setInterval(() => {
      const buffered = streamBufferRef.current;
      const msgId = streamingMessageIdRef.current;
      if (!buffered || !msgId) return;

      // Move buffer contents into React state and reset the buffer.
      streamBufferRef.current = '';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, content: m.content + buffered } : m
        )
      );
    }, 16); // ~60 fps
  }, []);

  const stopFlushInterval = useCallback(() => {
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }
    // Final flush: push any remaining buffered text into state.
    const buffered = streamBufferRef.current;
    const msgId = streamingMessageIdRef.current;
    if (buffered && msgId) {
      streamBufferRef.current = '';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, content: m.content + buffered } : m
        )
      );
    }
  }, []);

  // Clean up on unmount.
  useEffect(() => {
    return () => {
      if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+B toggles the conversation history drawer.
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setDrawerOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  // ---- Send message (streaming with non-streaming fallback) ----

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isStreaming) return;

    invalidateRecordToken();                    // a late transcript won't land in the now-sent draft (Codex P1-6)
    stopTts();
    ttsUnlock();                                // best-effort: keep iOS speech authorized for auto-read
    if (voice.state === 'recording') voice.abort();

    const messageText = inputMessage.trim();

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };

    // Add user message immediately.
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setError(null);
    setIsLoading(true);
    setStreamingPhase('searching');

    const assistantMessageId = `assistant-stream-${Date.now()}`;
    const request = {
      message: messageText,
      conversation_id: currentConversationId || undefined,
    };

    // --- Attempt streaming first ---
    try {
      // Insert an empty assistant bubble that will fill in token by token.
      const placeholderMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, placeholderMessage]);
      setIsLoading(false);
      setIsStreaming(true);

      streamBufferRef.current = '';
      fullAnswerRef.current = '';   // reset full-answer accumulator for this send (Codex P1-7)
      streamingMessageIdRef.current = assistantMessageId;
      firstTokenRef.current = true;
      startFlushInterval();

      // Wrap the streaming call in a Promise so we can await its completion
      // or catch errors and fall back to the non-streaming endpoint.
      await new Promise<void>((resolve, reject) => {
        const controller = chatApi.sendMessageStream(request, {
          onRewriting: () => {
            setStreamingPhase('rewriting');
          },
          onRewritten: () => {
            setStreamingPhase('searching');
          },
          onSources: (sources) => {
            setStreamingPhase('generating');
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, search_results: sources }
                  : m
              )
            );
          },
          onToken: (token) => {
            if (firstTokenRef.current) {
              setStreamingPhase('streaming');
              firstTokenRef.current = false;
            }
            streamBufferRef.current += token;
            fullAnswerRef.current += token;   // complete answer for auto-read (Codex P1-7)
          },
          onDone: (data) => {
            stopFlushInterval();
            streamingMessageIdRef.current = null;
            setStreamingPhase(null);
            setCurrentConversationId(data.conversation_id);

            // Update the message ID to the real server-assigned one.
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, id: `assistant-${data.message_id}` }
                  : m
              )
            );

            // Best-effort auto-read (Codex P1-7): a PURE call OUTSIDE the state
            // updater above, reading the full accumulated answer. speak() cancels-first,
            // so a StrictMode double-invoke can't double-speak.
            if (autoRead && fullAnswerRef.current.trim()) {
              ttsSpeak(fullAnswerRef.current, {
                onBlocked: () => toast('Tap 🔊 to play the answer', { icon: '🔇' }),
              });
            }

            loadConversations();
            resolve();
          },
          onError: (err) => {
            stopFlushInterval();
            streamingMessageIdRef.current = null;
            setStreamingPhase(null);
            reject(err);
          },
        });
        abortControllerRef.current = controller;
      });

      setIsStreaming(false);
    } catch (streamError: any) {
      // Streaming failed — clean up the empty/partial assistant bubble
      // and fall back to the non-streaming endpoint.
      console.warn('Streaming failed, falling back to non-streaming:', streamError);
      stopFlushInterval();
      streamingMessageIdRef.current = null;
      setIsStreaming(false);
      setStreamingPhase(null);

      // Remove the placeholder streaming message.
      setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId));
      setIsLoading(true);

      try {
        const response: ChatResponse = await chatApi.sendMessage(request);

        const assistantMessage: ChatMessage = {
          id: `assistant-${response.message_id}`,
          role: 'assistant',
          content: response.message || 'No response received',
          timestamp: new Date().toISOString(),
          search_results: response.search_results,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setCurrentConversationId(response.conversation_id);

        // Best-effort auto-read of the non-streaming answer (Codex P1-7) —
        // OUTSIDE the state updaters above.
        if (autoRead && response.message?.trim()) {
          ttsSpeak(response.message, {
            onBlocked: () => toast('Tap 🔊 to play the answer', { icon: '🔇' }),
          });
        }

        await loadConversations();
      } catch (fallbackError: any) {
        console.error('Fallback (non-streaming) also failed:', fallbackError);

        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `Error: ${
            fallbackError.response?.data?.detail ||
            fallbackError.message ||
            'Failed to send message'
          }`,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, errorMessage]);
        setError('Failed to send message. Please try again.');
      } finally {
        setIsLoading(false);
        setStreamingPhase(null);
      }
    }
  };

  const handleFeedback = async (messageId: number, score: number) => {
    try {
      await chatApi.submitFeedback(messageId, score);
      setMessages(prev => prev.map(msg =>
        msg.id === `assistant-${messageId}` ? { ...msg, feedbackScore: score } : msg
      ));
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const isBusy = isLoading || isStreaming;
  // The personalized home/empty state shows until the first message exists.
  const isHome = messages.length === 0;

  // Quick-action chip: prefill a query scaffold and focus the input (no auto-send).
  const handlePickAction = (scaffold: string) => {
    // Discard any in-flight voice transcript so it can't land in the scaffold
    // (same race guard as send / new chat / conversation switch — Codex P2).
    invalidateRecordToken();
    setInputMessage(scaffold);
    // Defer focus until the controlled value has painted so the caret lands at the end.
    requestAnimationFrame(() => chatInputRef.current?.focus());
  };

  // ---- Voice input (STT) ----
  // Monotonic token: bumped on send, new chat, and conversation switch. A
  // transcript is only inserted if the token is unchanged since record-start
  // (Codex P1-6 — covers same-conversation send/edit, new-chat null state,
  // and conversation switch, not just a conversation-id change).
  const recordTokenRef = useRef(0);
  const recordStartTokenRef = useRef<number | null>(null);
  // SINGLE TTS source (Task 16): replaces Task 11's two-field destructure so
  // stopTts (Task 11 usages) + speak/unlock (auto-read) all come from one hook call.
  const { user } = useAuth();
  const { autoRead } = useVoicePreferences(user?.id ?? null);
  const { speak: ttsSpeak, stop: stopTts, unlock: ttsUnlock } = useSpeechSynthesis();

  const invalidateRecordToken = () => { recordTokenRef.current += 1; };

  const voice = useVoiceInput({
    maxRecordSeconds: 20,
    onError: (msg) => toast.error(msg),
    onTranscript: (text) => {
      // Race guard: insert only if nothing send/navigation-worthy happened
      // since record-start.
      if (recordStartTokenRef.current !== recordTokenRef.current) {
        toast('Transcript ready — tap mic to insert', { icon: '🎤' });
        return;
      }
      setInputMessage((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
    },
  });

  const handleVoiceStart = () => {
    if (shouldShowVoiceNotice()) {               // one-time mic privacy notice (spec §9)
      markVoiceNoticeSeen();
      toast('Audio is sent to OpenAI for transcription and not stored.', { duration: 5000, icon: '🎤' });
    }
    stopTts();                                   // starting a recording stops TTS (Codex #9)
    recordStartTokenRef.current = recordTokenRef.current;
    voice.start();
  };

  // ---- Photo scan-to-fill ----
  const [photoState, setPhotoState] = useState<'idle' | 'extracting'>('idle');

  const handlePickPhoto = async (file: File) => {
    // Race guard (Codex P1-5): the extract is async (vision call ~3-8s). If the
    // user sends, starts a new chat, switches conversation, or picks a chip
    // while it's in flight, the result must NOT land in the new draft. Capture
    // the monotonic record token at pick time and only append if it's unchanged
    // when the result arrives — the SAME mechanism voice uses (recordStartToken
    // vs recordToken). Also bump it now so any in-flight VOICE transcript is
    // discarded (a photo pick is a navigation-worthy action too).
    invalidateRecordToken();
    const pickToken = recordTokenRef.current;
    setPhotoState('extracting');
    try {
      const { text } = await extractFromPhoto(file);
      if (recordTokenRef.current !== pickToken) {
        // Something send/navigation-worthy happened mid-extract — don't clobber.
        toast('Photo text ready — retake to use it', { icon: '📷' });
        return;
      }
      // Append to whatever is typed (mirrors the voice onTranscript append).
      setInputMessage((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
      requestAnimationFrame(() => chatInputRef.current?.focus());
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg =
        status === 413 ? 'That photo is too large.'
        : status === 415 ? "Couldn't read that image format."
        : status === 429 ? 'Too many photos — wait a moment.'
        : status === 422 ? "Couldn't read that — try a clearer, closer shot."
        : "Couldn't read the photo — try again.";
      toast.error(msg);
    } finally {
      // Only clear the spinner if THIS extract is still the current one. A
      // superseded extract (user navigated, then started a NEW extract) must
      // not let its late finally hide the newer extract's spinner / re-enable
      // the button (Codex P2). Nav handlers reset photoState themselves.
      if (recordTokenRef.current === pickToken) setPhotoState('idle');
    }
  };

  const startNewConversation = () => {
    invalidateRecordToken();   // a transcript resolving after a new-chat reset is discarded (Codex P1-6)
    setPhotoState('idle');     // clear a mid-extract spinner so it doesn't bleed onto the new composer
    setMessages([]);
    setCurrentConversationId(null);
    setDrawerOpen(false);
    navigate('/chat');
  };

  const handleSelectConversation = (id: number) => {
    invalidateRecordToken();   // a transcript resolving after a conversation switch is discarded (Codex P1-6)
    setPhotoState('idle');     // clear a mid-extract spinner so it doesn't bleed onto the switched-to composer
    loadConversationMessages(id);
    setDrawerOpen(false);
    // Update URL without triggering a full re-render so the drawer transition completes
    navigate(`/chat/${id}`, { replace: true });
  };

  // Handle document preview
  const handleDocumentPreview = (result: any) => {
    const docName = result.document_name || result.document_title || 'Unknown Document';
    setPreviewDocument({
      id: result.document_id,
      name: docName,
      pageNumber: result.page_number || result.page
    });
    setIsPDFViewerOpen(true);
  };

  // Close PDF viewer
  const closePDFViewer = () => {
    setIsPDFViewerOpen(false);
    setPreviewDocument(null);
  };

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Conversation history drawer (overlay on desktop + mobile) */}
      <Transition.Root show={drawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/40 lg:bg-gray-900/20" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 lg:ml-16">
                <div className="flex grow flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 shadow-xl overflow-y-auto">
                  <ConversationList
                    conversations={conversations}
                    activeConversationId={currentConversationId}
                    onNewChat={startNewConversation}
                    onSelectConversation={handleSelectConversation}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Chat area — warm cream surface on the home/empty state, standard otherwise. */}
      <div className={clsx(
        'flex-1 flex flex-col min-w-0 min-h-0',
        isHome && 'bg-air-cream dark:bg-air-cream-dark',
      )}>
        {/* Minimal top bar: History (left) · AIRAssist brandmark (center) · New Chat (right).
            flex-shrink-0 so it never compresses; the messages region (min-h-0) is the
            sole scroll container, keeping this header pinned on long conversations. */}
        <div className={clsx(
          'flex flex-shrink-0 items-center justify-between px-3 py-3',
          isHome
            ? 'bg-air-cream dark:bg-air-cream-dark'
            : 'border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900',
        )}>
          <button
            onClick={() => setDrawerOpen(true)}
            title="Conversation history (⌘B)"
            aria-label="Open conversation history"
            className="flex h-10 w-10 items-center justify-center rounded-full text-air-navy hover:bg-air-navy/10 transition-colors dark:text-air-skytext dark:hover:bg-white/10"
          >
            <PanelLeft className="w-5 h-5" />
          </button>

          {/* Brandmark: the AIR Control Concepts logo with "Assist" joined → AIRAssist. */}
          <div className="flex items-center select-none" aria-label="AIRAssist">
            <img
              src="/AIRlogo.png"
              alt=""
              aria-hidden="true"
              className="h-5 w-auto dark:brightness-0 dark:invert"
            />
            <span className="-ml-0.5 text-lg font-semibold tracking-tight text-air-navy dark:text-gray-100">
              Assist
            </span>
          </div>

          <button
            onClick={startNewConversation}
            title="Start a new conversation"
            aria-label="New chat"
            className="flex h-10 w-10 items-center justify-center rounded-full text-air-navy hover:bg-air-navy/10 transition-colors dark:text-air-skytext dark:hover:bg-white/10"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Messages area — min-h-0 lets this flex child shrink so overflow-y-auto
            scrolls INTERNALLY (header stays put); overflow-x-hidden guards sideways scroll. */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4">
          {isHome && (
            <ChatHomeState
              fullName={user?.full_name ?? null}
              username={user?.username ?? null}
              onPickAction={handlePickAction}
              disabled={isBusy}
            />
          )}

          {messages.map((message, index) => {
            const isLastAssistant = message.role === 'assistant' && index === messages.length - 1;
            return (
              <MessageBubble
                key={message.id}
                role={message.role}
                content={message.content}
                searchResults={message.search_results}
                isStreaming={isLastAssistant && isStreaming}
                streamingPhase={isLastAssistant && isStreaming ? streamingPhase || undefined : undefined}
                onSourceClick={(docId, docName, pageNum) => handleDocumentPreview({
                  document_id: docId,
                  document_name: docName,
                  page_number: pageNum,
                })}
                messageId={
                  message.role === 'assistant' && message.id.startsWith('assistant-')
                    ? (() => { const n = parseInt(message.id.replace('assistant-', '')); return isNaN(n) ? undefined : n; })()
                    : undefined
                }
                feedbackScore={message.feedbackScore}
                onFeedback={handleFeedback}
              />
            );
          })}

          {isLoading && !isStreaming && (
            <MessageBubble
              role="assistant"
              content=""
              isStreaming={true}
              streamingPhase="searching"
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mb-2 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Chat input */}
        <ChatInput
          ref={chatInputRef}
          value={inputMessage}
          onChange={setInputMessage}
          onSend={handleSendMessage}
          disabled={isBusy}
          voiceSupported={voice.supported}
          voiceState={voice.state}
          micLevel={voice.micLevel}
          onVoiceStart={handleVoiceStart}
          onVoiceStop={voice.stop}
          onPickPhoto={handlePickPhoto}
          photoState={photoState}
        />
      </div>

      {/* PDF Preview Modal */}
      {isPDFViewerOpen && previewDocument && (
        <Suspense fallback={null}>
          <PDFViewer
            documentId={previewDocument.id}
            fileName={previewDocument.name}
            pageNumber={previewDocument.pageNumber}
            onClose={closePDFViewer}
          />
        </Suspense>
      )}
    </div>
  );
};
